import { useState } from 'react';
import { useLocation, useNavigate, Outlet } from 'react-router-dom';
import { AppBar, AppBarSection, AppBarSpacer, Drawer, DrawerContent } from '@progress/kendo-react-layout';
import type { DrawerSelectEvent, DrawerItemProps } from '@progress/kendo-react-layout';
import { Button } from '@progress/kendo-react-buttons';
import { useAdminAuth } from '../lib/adminAuth';
import { RegionPicker } from './RegionPicker';
import './AppShell.css';
import { useMediaQuery, WIDE_QUERY } from '@shared/theme';

interface NavEntry {
  text: string;
  route: string;
  icon: string;
}

const NAV: NavEntry[] = [
  { text: 'Home', route: '/', icon: 'k-i-home' },
  { text: 'Tenants', route: '/tenants', icon: 'k-i-grid' },
  { text: 'Migrations', route: '/migrations', icon: 'k-i-refresh' },
  { text: 'Email templates', route: '/email-templates', icon: 'k-i-email' },
  { text: 'System settings', route: '/system-settings', icon: 'k-i-gears' },
  { text: 'Model catalog', route: '/model-catalog', icon: 'k-i-cog' },
  { text: 'Platform admins', route: '/platform-admins', icon: 'k-i-user' },
  { text: 'Misc ops', route: '/misc-ops', icon: 'k-i-gear' },
];

export function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const { state, signOut } = useAdminAuth();
  // Same sidebar rule as the customer shell (AppShell.tsx there): the Drawer
  // auto-collapses to its `mini` rail below 1024px, and an explicit brand-toggle
  // press wins without being persisted. Admin runs the same 768px floor.
  //
  // The override records WHICH ROUTE it was made on. AppShell stays mounted
  // across admin routes, so without that the expanded Drawer would follow you to
  // the destination and sit on top of the page you just asked for — at `medium`
  // an expanded Drawer is a temporary state, not a preference. At `wide` it is a
  // permanent column, so the override applies everywhere.
  const isWide = useMediaQuery(WIDE_QUERY);
  const [navOverride, setNavOverride] = useState<{ expanded: boolean; path: string } | null>(null);
  const overrideApplies =
    navOverride !== null && (isWide || navOverride.path === location.pathname);
  const expanded = overrideApplies ? navOverride.expanded : isWide;

  const items: (DrawerItemProps & NavEntry)[] = NAV.map((entry) => ({
    ...entry,
    selected: entry.route === '/'
      ? location.pathname === '/'
      : location.pathname.startsWith(entry.route),
  }));

  const onSelect = (e: DrawerSelectEvent) => {
    const target = items[e.itemIndex];
    if (target) navigate(target.route);
  };

  const userEmail = state.status === 'authenticated' ? state.admin.email : '';

  return (
    <div className="app-shell">
      <AppBar className="app-shell-bar">
        <AppBarSection>
          <button
            type="button"
            className="brand-toggle"
            aria-label="Toggle navigation"
            aria-expanded={expanded}
            onClick={() => setNavOverride({ expanded: !expanded, path: location.pathname })}
          >
            <img
              className="brand-mark"
              src="/icons/light/automationai-48.png"
              alt=""
              aria-hidden="true"
              width={24}
              height={24}
            />
            <span className="brand-name">Automations — Admin</span>
          </button>
        </AppBarSection>
        <AppBarSpacer />
        <AppBarSection>
          <RegionPicker />
        </AppBarSection>
        <AppBarSection>
          <span className="user-email">{userEmail}</span>
          <Button fillMode="flat" size="small" onClick={() => void signOut().then(() => navigate('/login'))}>
            Sign out
          </Button>
        </AppBarSection>
      </AppBar>

      <Drawer
        expanded={expanded}
        position="start"
        mode="push"
        mini
        items={items}
        onSelect={onSelect}
        className="app-shell-drawer"
      >
        <DrawerContent>
          <main className="app-shell-main">
            <Outlet />
          </main>
        </DrawerContent>
      </Drawer>
    </div>
  );
}
